import React, { createContext, useCallback, useContext, useEffect } from 'react';

import { CognitoUserSession } from 'amazon-cognito-identity-js';
import { useNavigate } from 'react-router-dom';

import { IBaseProps } from '@rbi-ctg/frontend';
import { ICommunicationPreference, IFavoriteStore } from 'generated/rbi-graphql';
import useErrorModal from 'hooks/use-error-modal';
import { getCurrentSession } from 'remote/auth';
import { useNetworkContext } from 'state/network';
import { isKiosk } from 'utils/environment';
import LocalStorage, { StorageKeys } from 'utils/local-storage';
import { routes } from 'utils/routing';

import {
  ISignIn,
  ISignUpResult,
  useAccountAuthentication
} from './hooks/use-account-authentication';
import { UserDetails, useCurrentUser } from './hooks/use-current-user';

export enum BooleanAsString {
  True = 'true',
  False = 'false'
}

export type CommunicationPreferences = Array<{
  id: string;
  value: string | BooleanAsString.True | BooleanAsString.False;
}>;

export type FavoriteStore = {
  storeId: string;
};

export type FavoriteStores = Array<FavoriteStore>;

export type UpdateUserInfo = {
  dob: string;
  name: string;
  phoneNumber: string;
  promotionalEmails: boolean;
  isoCountryCode: string;
  zipcode: string;
  defaultReloadAmt: number;
  defaultCheckoutPaymentMethodId?: string;
  defaultReloadPaymentMethodId?: string;
  autoReloadEnabled: boolean;
  autoReloadThreshold: number;
};

type SignupArgs = {
  email: string;
  name: string;
  phoneNumber: string;
  country: string;
  wantsPromotionalEmails: boolean;
};

export interface AuthInterface {
  // Computed values
  loading: boolean;
  originLocation: null | string;
  user: null | UserDetails;
  // Functions
  getCurrentSession(): Promise<null | CognitoUserSession>;
  refreshCurrentUser(): Promise<void>;
  isAuthenticated(): boolean;
  setOriginLocation(location: null | string): void;
  signIn(args: ISignIn): Promise<void>;
  signOut(): Promise<void>;
  signUp(
    args: SignupArgs,
    signInOverride?: (args: ISignIn) => Promise<void>
  ): Promise<ISignUpResult>;
  updateUserInfo(args: UpdateUserInfo): Promise<void>;
  updateUserCommPrefs(commPrefs: Array<ICommunicationPreference>): Promise<void>;
  updateUserFavStores(favStores: Array<IFavoriteStore>): Promise<void>;
  validateLogin(args: { jwt: string; username: string }): Promise<void>;
  validateLoginOtp(args: { otpCode: string }): Promise<void>;
  userHasSignedIn: boolean;
}

export const AuthContext = createContext<AuthInterface>(({} as any) as AuthInterface);
export const useAuthContext = () => useContext(AuthContext);

export const AuthProvider: React.FC<IBaseProps> = ({ children }) => {
  const { hasNotAuthenticatedError, setHasNotAuthenticatedError } = useNetworkContext();
  const navigate = useNavigate();

  const [ErrorDialog, openErrorDialog] = useErrorModal({
    onConfirm: () => navigate(routes.signIn, { replace: true }),
    modalAppearanceEventMessage: 'Error: Auth Error'
  });

  const {
    refreshCurrentUser,
    setCurrentUser,
    updateUserCommPrefs,
    updateUserFavStores,
    updateUserInfo,
    user,
    userHasSignedIn,
    userLoading
  } = useCurrentUser({
    openErrorDialog
  });
  const {
    authLoading,
    originLocation,
    setOriginLoc,
    signIn,
    signOut,
    signUp,
    validateLogin,
    validateLoginOtp
  } = useAccountAuthentication({
    refreshCurrentUser,
    isKiosk,
    openErrorDialog,
    setCurrentUser
  });

  const getCtx = useCallback(() => {
    return {
      // Computed values
      loading: authLoading || userLoading,
      originLocation,
      user,
      // Functions
      getCurrentSession,
      refreshCurrentUser,
      isAuthenticated: () => !!user,
      signIn,
      signOut,
      signUp,
      updateUserInfo,
      updateUserCommPrefs,
      updateUserFavStores,
      validateLogin,
      validateLoginOtp,
      userHasSignedIn,
      setOriginLocation: (args: null | string) => setOriginLoc(args)
    };
  }, [
    authLoading,
    userLoading,
    originLocation,
    user,
    refreshCurrentUser,
    signIn,
    signOut,
    signUp,
    updateUserInfo,
    updateUserCommPrefs,
    updateUserFavStores,
    validateLogin,
    validateLoginOtp,
    userHasSignedIn,
    setOriginLoc
  ]);

  useEffect(() => {
    // save redirect url so user can get redirected correctly after login
    LocalStorage.setItem(StorageKeys.AUTH_REDIRECT, { callbackUrl: originLocation });
  }, [originLocation]);

  useEffect(() => {
    if (hasNotAuthenticatedError) {
      // TODO: Re-enable once https://github.com/aws-amplify/amplify-js/pull/4205
      // to make sure we stop sending requests with expired tokens
      // signOut();

      setHasNotAuthenticatedError(false);
    }
  }, [hasNotAuthenticatedError, setHasNotAuthenticatedError]);

  return (
    <AuthContext.Provider value={getCtx()}>
      {children}
      <ErrorDialog />
    </AuthContext.Provider>
  );
};

export default AuthContext.Consumer;
